<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import BookingCard from 'dashboard/routes/dashboard/conversation/contact/BookingCard.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));
const booking = computed(() => session.value.booking);
const uiFlags = computed(() => carBooking.uiFlags);

const editing = ref(false);
const savedMessage = ref('');
const edit = ref({
  flightNumber: '',
  firstName: '',
  lastName: '',
  email: '',
});

const startEdit = () => {
  savedMessage.value = '';
  // Driver/flight live inside the gateway booking snapshot (booking.details).
  const details = booking.value?.details || {};
  const driver = details.driver || {};
  edit.value = {
    flightNumber: details.flightNumber || '',
    firstName: driver.firstName || '',
    lastName: driver.lastName || '',
    email: driver.email || '',
  };
  editing.value = true;
};

const cancelEdit = () => {
  editing.value = false;
};

const save = async () => {
  const driver = {};
  if (edit.value.firstName) driver.firstName = edit.value.firstName;
  if (edit.value.lastName) driver.lastName = edit.value.lastName;
  if (edit.value.email) driver.email = edit.value.email;

  const changes = {};
  if (edit.value.flightNumber) changes.flightNumber = edit.value.flightNumber;
  if (Object.keys(driver).length) changes.driver = driver;

  const result = await carBooking.modify(
    props.conversationId,
    booking.value.id,
    changes
  );

  savedMessage.value = result?.rebooked
    ? t('CONVERSATION.CAR_BOOKING.BOOKED.REBOOKED', {
        reference: session.value.booking?.reference_number,
      })
    : t('CONVERSATION.CAR_BOOKING.BOOKED.SAVED');
  editing.value = false;
};

const cancel = () => carBooking.cancel(props.conversationId, booking.value.id);

const reset = () => carBooking.reset(props.conversationId);
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-1 flex-col gap-4 overflow-y-auto">
      <div
        class="flex items-center gap-2 text-sm font-semibold text-n-green-11"
      >
        <span class="i-lucide-circle-check size-4" />
        {{ t('CONVERSATION.CAR_BOOKING.BOOKED.TITLE') }}
      </div>

      <div
        v-if="booking"
        class="overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
      >
        <BookingCard :booking="booking" />
      </div>

      <p v-if="savedMessage" class="m-0 text-sm text-n-green-11">
        {{ savedMessage }}
      </p>

      <div
        v-if="editing"
        class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-surface-1 p-3.5"
      >
        <p
          class="m-0 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.BOOKED.EDIT') }}
        </p>
        <NextInput
          :model-value="edit.flightNumber"
          :label="t('CONVERSATION.CAR_BOOKING.BOOKED.FLIGHT_NUMBER')"
          :placeholder="
            t('CONVERSATION.CAR_BOOKING.BOOKED.FLIGHT_NUMBER_PLACEHOLDER')
          "
          @update:model-value="edit.flightNumber = $event"
        />
        <NextInput
          :model-value="edit.firstName"
          :label="t('CONVERSATION.CAR_BOOKING.BOOKED.FIRST_NAME')"
          :placeholder="
            t('CONVERSATION.CAR_BOOKING.BOOKED.FIRST_NAME_PLACEHOLDER')
          "
          @update:model-value="edit.firstName = $event"
        />
        <NextInput
          :model-value="edit.lastName"
          :label="t('CONVERSATION.CAR_BOOKING.BOOKED.LAST_NAME')"
          :placeholder="
            t('CONVERSATION.CAR_BOOKING.BOOKED.LAST_NAME_PLACEHOLDER')
          "
          @update:model-value="edit.lastName = $event"
        />
        <NextInput
          :model-value="edit.email"
          type="email"
          :label="t('CONVERSATION.CAR_BOOKING.BOOKED.EMAIL')"
          :placeholder="t('CONVERSATION.CAR_BOOKING.BOOKED.EMAIL_PLACEHOLDER')"
          @update:model-value="edit.email = $event"
        />
        <div class="flex items-center gap-2">
          <NextButton
            faded
            slate
            sm
            :label="t('CONVERSATION.CAR_BOOKING.BOOKED.CANCEL_EDIT')"
            :disabled="uiFlags.modifying"
            @click="cancelEdit"
          />
          <NextButton
            solid
            blue
            sm
            class="flex-1"
            :label="t('CONVERSATION.CAR_BOOKING.BOOKED.SAVE')"
            :disabled="uiFlags.modifying"
            :is-loading="uiFlags.modifying"
            @click="save"
          />
        </div>
      </div>

      <NextButton
        v-else
        faded
        slate
        sm
        icon="i-lucide-pencil"
        class="self-start"
        :label="t('CONVERSATION.CAR_BOOKING.BOOKED.EDIT')"
        :disabled="booking && booking.status === 'cancelled'"
        @click="startEdit"
      />
    </div>

    <div
      class="mt-4 flex items-center gap-3 border-t border-n-weak bg-n-surface-2 px-4 py-3"
    >
      <NextButton
        solid
        ruby
        :label="t('CONVERSATION.CAR_BOOKING.BOOKED.CANCEL')"
        :disabled="booking && booking.status === 'cancelled'"
        @click="cancel"
      />
      <NextButton
        faded
        slate
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.BOOKED.NEW')"
        @click="reset"
      />
    </div>
  </div>
</template>
